import React from "react";
import { ANY, footOptions, licenceOptions, nationalityOptions } from "./pitchFilters";

/**
 * The filter panel. It FILTERS — it does not merely render.
 *
 * Everything except the Free-Agents star narrows the cards already loaded for
 * the active tab, because the list endpoint accepts exactly one filter
 * parameter and this rebuild may not invent query parameters. The star is that
 * one parameter: it is the club's existing `free_agents_only=true` filter, moved
 * out of its lone toggle above the list and into this panel, and it still
 * reloads from the server.
 *
 * Dropdown options come from the pitches on screen, never from a hard-coded
 * list: an enum that does not know a value hides cards the club can see.
 * A dropdown with nothing to offer is not rendered at all.
 */
const inputClass =
  "min-h-[44px] min-w-0 rounded-lg border border-[#E5E7EB] bg-white px-2.5 text-[14px] text-[#111827]";

const RangeFilter = ({ label, fromValue, toValue, onFrom, onTo, compact }) => (
  <div className={`flex min-w-0 flex-col gap-1.5 ${compact ? "w-full" : ""}`}>
    <span className="text-[13px] font-semibold text-[#374151]">{label}</span>
    <div className="flex min-w-0 items-center gap-1.5">
      <input
        type="number"
        inputMode="numeric"
        aria-label={`${label} from`}
        placeholder="from"
        value={fromValue}
        onChange={(event) => onFrom(event.target.value)}
        className={`${inputClass} ${compact ? "w-full flex-1" : "w-[104px]"}`}
      />
      <span className="flex-shrink-0 text-[13px] text-[#6B7280]">to</span>
      <input
        type="number"
        inputMode="numeric"
        aria-label={`${label} to`}
        placeholder="to"
        value={toValue}
        onChange={(event) => onTo(event.target.value)}
        className={`${inputClass} ${compact ? "w-full flex-1" : "w-[104px]"}`}
      />
    </div>
  </div>
);

const SelectFilter = ({ label, value, options, onChange, compact }) => (
  <div className={`flex min-w-0 flex-col gap-1.5 ${compact ? "w-full" : ""}`}>
    <span className="text-[13px] font-semibold text-[#374151]">{label}</span>
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`${inputClass} ${compact ? "w-full" : ""}`}
    >
      <option value={ANY}>{ANY}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
);

const PitchFilterPanel = ({
  filters,
  setFilter,
  onReset,
  pitches,
  isStaffRequest,
  showFreeAgentsToggle,
  freeAgentsOnly,
  onToggleFreeAgents,
  compact = false,
}) => {
  const nationalities = nationalityOptions(pitches);
  const licences = licenceOptions(pitches);
  const feet = footOptions(pitches);

  return (
    <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-[0_1px_3px_rgba(17,24,39,0.06)]">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3.5">
        <RangeFilter
          label="Age"
          compact={compact}
          fromValue={filters.ageMin}
          toValue={filters.ageMax}
          onFrom={(value) => setFilter("ageMin", value)}
          onTo={(value) => setFilter("ageMax", value)}
        />

        {isStaffRequest ? (
          <RangeFilter
            label="Experience (years)"
            compact={compact}
            fromValue={filters.experienceMin}
            toValue={filters.experienceMax}
            onFrom={(value) => setFilter("experienceMin", value)}
            onTo={(value) => setFilter("experienceMax", value)}
          />
        ) : (
          <>
            <RangeFilter
              label="Height (cm)"
              compact={compact}
              fromValue={filters.heightMin}
              toValue={filters.heightMax}
              onFrom={(value) => setFilter("heightMin", value)}
              onTo={(value) => setFilter("heightMax", value)}
            />
            <RangeFilter
              label="Market value (€)"
              compact={compact}
              fromValue={filters.marketMin}
              toValue={filters.marketMax}
              onFrom={(value) => setFilter("marketMin", value)}
              onTo={(value) => setFilter("marketMax", value)}
            />
            <RangeFilter
              label="Transfer fee (€)"
              compact={compact}
              fromValue={filters.feeMin}
              toValue={filters.feeMax}
              onFrom={(value) => setFilter("feeMin", value)}
              onTo={(value) => setFilter("feeMax", value)}
            />
          </>
        )}

        {!isStaffRequest && feet.length > 0 ? (
          <SelectFilter
            label="Foot"
            compact={compact}
            value={filters.foot}
            options={feet}
            onChange={(value) => setFilter("foot", value)}
          />
        ) : null}

        {isStaffRequest && licences.length > 0 ? (
          <SelectFilter
            label="Licence"
            compact={compact}
            value={filters.licence}
            options={licences}
            onChange={(value) => setFilter("licence", value)}
          />
        ) : null}

        {nationalities.length > 0 ? (
          <SelectFilter
            label="Nationality"
            compact={compact}
            value={filters.nationality}
            options={nationalities}
            onChange={(value) => setFilter("nationality", value)}
          />
        ) : null}

        {showFreeAgentsToggle ? (
          <button
            type="button"
            role="switch"
            aria-checked={freeAgentsOnly}
            onClick={onToggleFreeAgents}
            title="Show only pitches submitted without a transfer fee"
            className={`inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-full border px-4 text-[14px] font-semibold ${
              freeAgentsOnly
                ? "border-[#2183FE] bg-[#EFF6FF] text-[#1D4ED8]"
                : "border-[#E5E7EB] bg-white text-[#374151]"
            } ${compact ? "w-full justify-center" : ""}`}
          >
            <span aria-hidden="true">★</span>
            Free Agents only
          </button>
        ) : null}

        <button
          type="button"
          onClick={onReset}
          className={`min-h-[44px] whitespace-nowrap rounded-lg px-3.5 text-[14px] font-semibold text-[#2183FE] hover:bg-[#EFF6FF] ${
            compact ? "w-full" : "ml-auto"
          }`}
        >
          Reset filters
        </button>
      </div>
    </div>
  );
};

export default PitchFilterPanel;
